/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import AdobeExperienceCloud from 'url:@exc/brand-icons/hero/AdobeExperienceCloud.svg';
import AdobeExperiencePlatform from 'url:@exc/brand-icons/hero/AdobeExperiencePlatform.svg';
import AdobeExperienceSubCloud from 'url:@exc/brand-icons/hero/AdobeExperienceSubCloud.svg';
import {AuthMessages, ErrorTypes} from './enums';
import CoreContext from './context';
import {debounceRaF} from '@exc/dom';
import {getAvatarUrls, getOrgName, runInBackground, staleWhileRevalidate} from './utils';
import {getBestSupportedLocale} from '@exc/auth';
import {getContextService} from './services/ContextService/ContextService';
import {getEnabledSandbox} from './utils/sandbox';
import {getOmegaEvent} from '@exc/events';
import {
  getPathWithoutTenant,
  getQueryValue,
  getTenantFromPath,
  getValueFromPath,
  hashToPath,
  updatePath,
  updateValueInPath
} from '@exc/url';
import {getWorkHubResponse} from './services/WorkHubService';
import {hasPermission} from './utils/permission';
import HeroIcons from 'url:@exc/brand-icons/HeroIcons.svg';
import {history} from '@exc/router';
import {Internal} from '@adobe/exc-app/internal';
import metrics, {Level} from '@adobe/exc-app/metrics';
import {PREF_APPID, PREF_GROUPID} from '@exc/graphql';
import PropTypes from 'prop-types';
import Provider from '@react/react-spectrum/Provider';
import React from 'react';
import ServiceWorkerControllerService from './services/ServiceWorkerControllerService';
import {SettingsLevel} from '@adobe/exc-app/settings';
import ShellWrapper from './Shell';
import {storage} from '@exc/storage';
import TranslationService from './services/TranslationService';
import unifiedShellSession from './services/UnifiedShellSessionService';
import {withLocalization} from '@adobe/adobe-intl-react';

const NOOP = () => {};
const ErrorView = React.lazy(() => import('./modules/Error'));
const ShellNoAccess = React.lazy(() => import('./ShellNoAccess'));

const LAST_LOGGED_IN = 'LAST_LOGGED_IN';
const AVATAR_SIZES = [110, 115];
const TEN_HOURS = 1000 * 60 * 60 * 10;
const TEN_YEARS = 3600 * 1000 * 24 * 365 * 10;
const TOAST_TIMEOUT = 11000;
const UNIFIED_SHELL_APPID = 'shell';
const SUPPORTED_LOCALES = ['de-DE', 'en-US', 'es-ES', 'fr-FR', 'it-IT', 'ja-JP', 'ko-KR', 'pt-BR', 'zh-CN', 'zh-TW'];

const SHELL_SIDE_NAV_WIDTH = 222;
const SHELL_HEIGHT_WITH_SANDBOX = 80;
const SHELL_HEIGHT_WITHOUT_SANDBOX = 48;

export const AUTH_TYPE = {
  TYPE1: 'ind',
  TYPE2E: 'eoa',
  TYPE3: 'fed'
};

export const HERO_CONFIGS = {
  application: {
    favicon: AdobeExperienceSubCloud,
    icon: `${HeroIcons}#AdobeExperienceSubCloud`,
    shortTitle: 'Adobe Experience Cloud'
  },
  cloud: {
    favicon: AdobeExperienceCloud,
    icon: `${HeroIcons}#AdobeExperienceCloud`,
    path: '/home',
    shortTitle: 'Adobe Experience Cloud'
  },
  platform: {
    favicon: AdobeExperiencePlatform,
    icon: `${HeroIcons}#AdobeExperiencePlatform`,
    shortTitle: 'Adobe Experience Platform'
  },
  service: {
    favicon: AdobeExperienceCloud,
    icon: `${HeroIcons}#AdobeExperienceCloud`,
    shortTitle: 'Adobe Experience Cloud'
  }
};

const cleanTenant = tenant => tenant && tenant.split(':')[0];

class Core extends React.Component {
  // firstRender gives us an indicator that we are still in the process
  // of initial state changes and data fetching
  static firstRender = true;

  constructor(props) {
    super(props);

    this.config = props.config;
    this.metrics = metrics.create('exc.core.Core');
    this.serviceWorkerService = new ServiceWorkerControllerService();
    this.shellTimer = props.shellTimer || this.metrics.start('Shell');

    const hero = HERO_CONFIGS.cloud;
    this.favicon = hero.icon;
    this.nonSupportedBrowser = false;

    this.state = {
      accessToken: null,
      activeProductContext: null,
      auth: null,
      coachMark: {},
      coachMarkPosition: null,
      customEnvLabel: '',
      customHeroClick: false,
      customSearch: {},
      darkestTheme: false,
      defaultComponent: this.config.defaultComponent,
      enhancedImsOrgs: [],
      environment: this.config.environment,
      error: null,
      featureFlags: null,
      feedback: {},
      globalDialogOpen: false,
      helpCenter: {},
      hero,
      imsOrgs: [],
      locale: 'en-US',
      modal: false,
      nps: {
        enabled: false,
        sampling: 10,
        showToAdobeUsers: true
      },
      npsScore: null,
      onCustomButtonClick: NOOP,
      orgsMap: [],
      orgToSubOrgsMap: {},
      profile: null,
      sandbox: {
        enabled: false,
        onChange: this.onSandboxChange,
        sandboxes: [],
        selected: null
      },
      selectedImsOrg: null,
      selectedSubOrg: null,
      shellResponse: {},
      sidebar: {},
      solutionConfig: {},
      supportsViewportTakeover: false,
      userAccounts: null,
      viewportTakeover: false,
      workHubResponse: null,
      workspaces: []
    };

    this.getSideNavState();

    const {endpoints} = this.config;
    this.authPromise = new Promise(resolve => this.authResolve = resolve);
    this.translationService = new TranslationService(endpoints.translation);
    this.hasTenantInUrl = true;
    this.history = props.history || history;
    this.orgTenantMap = {};
    this.debouncedUpdateTargetNode = debounceRaF(this.updateTargetNode);
    this.tenantOrgMap = {};
    this.userFulfillableItems = {};
    this.userServiceCodes = {};
    this.unlisten = this.history.listen(this.onHistoryChange);
    this.settingsHandler = this.settingsHandler.bind(this);
    this.settingsRef = {
      settingsHandler: this.settingsHandler
    };
    this.htmlReady = undefined;

    // Tokens are handled in sessionstorage. If there are lingering local storage cached tokens,
    // remove them completely.
    storage.local.remove('shellCachedTokens');
  }

  async onAuthBroadcast({data = {}}) {
    const {auth} = this.state;
    switch (data.message) {
      case AuthMessages.LOGOUT:
        this.silentSignOut(true);
        break;
      case AuthMessages.LOGIN:
        // If session has changed refresh browser.
        if (this.sessionId && this.sessionId !== data.sessionId) {
          this.metrics.event('Auth.sessionReplaced', {
            newSession: data.sessionId,
            oldSession: this.sessionId
          });
          await Promise.all([
            Internal.flush(),
            // Moves Main component to a wait state in order to remove sandbox.
            // This will prevent an active iframe from blocking navigation.
            this.props.onLogout(),
            // On refresh IMSlib will see that the token is invalid and terminate the session
            // To prevent this, get a new token (Against new session) before refreshing the page
            // IMSlib stores it in session storage, so after reload it will be used by Shell.
            auth.refreshToken()]);
          location.reload();
        }
        break;
      default:
        data.message && this.metrics.warn(`Unknown auth message: ${data.message}`);
    }
  }

  async componentDidMount() {
    // Normally Core would be mounted with auth empty, but in case it's not - Run onAuth.
    this.props.auth && this.onAuth();
  }

  onAuth() {
    const {auth} = this.props;

    this.postAuth = import('./postAuth');
    // Check if the current browser is supported and show a message if it's not.
    this.postAuth.then(({DiscoveryService, getIframeEntry, getPerformanceMarks, permissionService, supportedBrowsers}) => {
      this.permissionService = permissionService;
      this.getPerformanceMarks = getPerformanceMarks;
      this.getIframeEntry = getIframeEntry;
      this.discoveryService = new DiscoveryService();
      if (!supportedBrowsers.test(window.navigator.userAgent)) {
        this.nonSupportedBrowser = true;
        this.metrics.warn('This browser is not supported.');
        this.showToast('browserlistWarning', {variant: 'info'});
      }

      this.authChannel = new BroadcastChannel('auth');
      this.authChannel.addEventListener('message', event => this.onAuthBroadcast(event));
    });

    this.setState({auth}, () => this.authProcessing());
    window.addEventListener('resize', this.debouncedUpdateTargetNode);
  }

  componentWillUnmount() {
    this.unlisten && this.unlisten();
    window.removeEventListener('resize', this.debouncedUpdateTargetNode);
  }

  componentDidUpdate() {
    if (this.props.error && !this.state.error) {
      this.setState({error: this.props.error});
    }
    // Process when we get auth.
    if (this.props.auth && !this.state.auth) {
      this.onAuth();
    }
  }

  async getSideNavState() {
    let sideNavOpen = await storage.session.get('sideNavOpen');
    if (sideNavOpen === null) {
      sideNavOpen = true;
    }
    this.setState({sideNavOpen});
    return sideNavOpen;
  }

  async setWorkHub() {
    const {workHubId} = this.state.solutionConfig.sideNav;
    const workHubResponse = await getWorkHubResponse(this.config.endpoints.ioGateway, this.sessionId, workHubId);
    this.setState({workHubResponse});
  }

  /**
   * Gets the Account Cluster data from GraphQL
   * @param {Promise<GraphQL Response>} userDataPromise Behance data
   * @param imsOrgs IMS orgs array
   * @param {IMSProfile} profile Current profile
   */
  async getAdditionalUserData(userDataPromise, imsOrgs, profile, locale) {
    const {getAccountClusterData} = await this.postAuth;
    const {imsAccountClusterData: accountCluster} = await userDataPromise.catch(() => {}) || {};
    const {enhancedImsOrgs, userAccounts} = getAccountClusterData(accountCluster, imsOrgs, locale, profile, this.props.formatMessage);

    if (this.state.showNoAccessShell && userAccounts) {
      this.metrics.info(`selectedImsOrg undefined in Core.js authProcessing, with ${userAccounts.length - 1} other account(s)`);
    }

    this.setState({enhancedImsOrgs, hasAccountClusterData: true, userAccounts});
  }

  /**
   * Gets the following data from GraphQL:
   *   1. Pulse (Timeline, Org Settings & Preferences) - To update the Shell header.
   *   2. Next profile - Only if we are using a cached profile (Reload), used to ensure a new open tab will always contain a fresh profile.
   *   3. Avatar (Via the User Data promise) - To update in both current and next profile with avatar, in case any SPA uses the avatar (i.e. Preferences)
   * @param {string} locale Current locale to get localized notifications.
   * @param {Promise<GraphQL Response>} userDataPromise Behance data
   * @param {IMSProfile} profile Current profile
   */
  async getHeaderData(locale, userDataPromise, profile) {
    const {auth} = this.state;
    const pulseDataPromise = runInBackground()
      .then(() => auth.gqlHandlerForHeader(locale, !this.props.isLogin));
    const {userBehance} = await userDataPromise.catch(() => {}) || {};
    const {avatar, avatarSrc: userAvatar} = getAvatarUrls(userBehance, AVATAR_SIZES);

    if (userAvatar && profile.avatarSrc !== userAvatar) {
      // Different or no avatar in profile. Update state.
      profile.avatar = avatar;
      profile.avatarSrc = userAvatar;
      this.setState({profile, userAvatar});
    }

    const {
      timeline: timelineResponse,
      userOrgPreferences,
      getSettings: settingsResponse,
      userProfileJson} = await pulseDataPromise || {};

    this.setState({pulseLoaded: true, settingsResponse, timelineResponse, userOrgPreferences});

    // In case we used a stored profile, we got a fresh profile and store it in the session.
    // This ensures on browser refresh profile will be fresh.
    if (userProfileJson) {
      if (userAvatar) {
        // Add the avatar to the profile we are caching.
        // If the user refresh the browser, the avatar will show right away.
        userProfileJson.avatar = avatar;
        userProfileJson.avatarSrc = userAvatar;
      }
      auth.updateProfile(userProfileJson);
    }
  }

  async getLaunchDarklyData(setState = true, selectedImsOrg = this.state.selectedImsOrg) {
    const ldDataTimer = this.metrics.start('LaunchDarklyData');
    const {auth, solutionConfig} = this.state;
    let featureFlags = await unifiedShellSession.get('featureFlags', null, selectedImsOrg);

    try {
      // This promise is used to post-fetch the launch darkly data from GQL in
      // the event that it already exists in local storage so that it will
      // always be up-to-date on refreshes without needing to log out to get
      // updated data. In the event that it's not in local storage, this promise
      // is awaited on so that it's available immediately.
      const getFlags = () => new Promise((resolve, reject) => {
        auth.gqlOrgLaunchDarkly().then(async ({imsOrgFeatureFlagSettings: {featureFlags}}) => {
          const flags = {};
          featureFlags.forEach(({name, value}) => (flags[name] = value));
          // Update unifiedShellSession with the flags for the current ims org so
          // that next time it can pull from storage.
          unifiedShellSession.update('featureFlags', flags, null, selectedImsOrg, true);
          resolve(flags);
        }).catch(() => reject());
      });
      featureFlags = await staleWhileRevalidate(getFlags, featureFlags);
    } catch (e) {
      this.metrics.error('Failed to retrieve feature flags from launch darkly via GQL');
      featureFlags = {};
      const access = solutionConfig.access || {};
      const flagsRequired = access.flag && (!access.code || access.codeFlagLogicOp === 'AND');
      if (flagsRequired) {
        this.showApiFailureErrorScreen('Feature Flag API failed.');
        throw e;
      }
    }

    this.appAssemblyService.setFeatureFlags(featureFlags);
    const shellResponse = this.getShellConfiguration(selectedImsOrg);
    const state = {featureFlags, shellResponse};
    setState && this.setState(state, () => this.onFeatureFlags());
    ldDataTimer.time('done', 'feature flags were pulled from GQL');
    return state;
  }

  configureNetwork(selectedImsOrg) {
    const {auth, sandbox, sideNavEnabled} = this.state;
    const {selected: selectedSandbox} = sandbox || {};
    const imsClientId = auth.getClientId();
    //configure network runtime API
    Internal.configureNetwork({
      apiGatewayUrl: this.config.endpoints.apiGateway,
      appId: UNIFIED_SHELL_APPID,
      getTokenInfo: async () => ({token: auth.fetchAccessToken()}),
      imsClientId,
      imsOrg: selectedImsOrg || this.state.selectedImsOrg,
      ioGatewayUrl: this.config.endpoints.ioGateway,
      ioRegionSpecificMap: this.config.endpoints.regionGatewayIoMap,
      sandbox: selectedSandbox,
      xqlGatewayUrl: this.config.endpoints.xql
    });
    selectedSandbox && this.permissionService?.setContext(selectedImsOrg || this.state.selectedImsOrg, selectedSandbox);
    sideNavEnabled && this.setWorkHub();
  }

  /**
   * Shows API failure error screen and sends metrics event.
   * @param {string} failureMessage Custom failure message
   */
  showApiFailureErrorScreen(failureMessage = 'API failed.') {
    this.metrics.event('Core.apiFailure', `${failureMessage} Showing message to user.`, {level: Level.ERROR});
    this.setState({error: ErrorTypes.UNKNOWN});
  }

  /**
   * Get user orgs from GraphQL with IMS fallback, and store to session.
   * @param {object} gqlResponse - GraphQL call response
   * @returns {Promise<{loginOrg, userOrgs}>} User Orgs
   */
  async getUserOrgData(gqlResponse) {
    const {auth} = this.state;
    let {getSettings = {}, userOrgs} = gqlResponse || {};
    const {defaultOrg = '', lastLoggedInOrg = '', userSandboxes = {}} = getSettings?.settings || {};
    userOrgs = userOrgs || await auth.fetchOrgs().catch(error => {
      this.metrics.error('Failed to get orgs data from both GraphQL and IMS.', {error});
      throw error;
    });
    const loginOrg = (defaultOrg && defaultOrg !== LAST_LOGGED_IN) ? defaultOrg : lastLoggedInOrg;
    userOrgs && unifiedShellSession.update('userOrgs', userOrgs, auth.getHashedUserId());
    return {
      lastLoggedInOrg,
      loginOrg,
      userOrgs,
      userSandboxes
    };
  }

  async authProcessing() {
    const authProcessingTimer = this.metrics.start('AuthProcessing');

    const {auth} = this.state;
    // Grab the org from the path and use that as the selected org. If there
    // is no org yet, use the selected one from IMS.
    let pathTenant = getTenantFromPath(this.history.location.pathname, true);

    // Configure network for GQL Orgs Data Request
    this.configureNetwork();
    // Fulfill authPromise so any processes awaiting it can start.
    // As of now this includes only custom tokens.
    this.authResolve();

    // Call GraphQL to fetch profile, org and spectrum theme.
    const getOrgsData = () => auth.gqlHandler(!this.props.isLogin).then(response => {
      // Save the settings data for future use
      const settings = response?.getSettings?.settings || null;
      settings && this.setState({settings}, () => this.userThemeHandler());
      return this.getUserOrgData(response);
    });
    const getOrgsMap = () => getOrgsData().then(data => data.userOrgs);

    // Also call GraphQL to fetch IMS account cluster data.
    const userDataPromise = auth.getAdditionalUserData(AVATAR_SIZES);
    const hashedUserId = auth.getHashedUserId();
    let orgsMap;
    let lastLoggedInOrg;
    let loginOrg;
    let profile;
    let userSandboxes;

    // This prevents checking the HTML through the service worker on app switch.
    if (this.htmlReady === undefined) {
      this.htmlReady = await this.serviceWorkerService.checkHtmlForUpdates(this.props.htmlReadyPromise, this.state.solutionConfig);
    }

    try {
      // If this is not a login but the userId is not in local storage this is the first
      // time the user has switched to this account in the current session. We should treat
      // it like a login in order to get the correct data from GQL for the current account.
      if (this.props.isLogin || !(await unifiedShellSession.get(hashedUserId))) {
        // Wait for GraphQL if this is a login.
        let orgsData = await getOrgsData();
        orgsMap = orgsData.userOrgs;
        loginOrg = orgsData.loginOrg;
        lastLoggedInOrg = orgsData.lastLoggedInOrg;
        userSandboxes = orgsData.userSandboxes;
      } else {
        const [cachedProfile, cachedOrgs] = await Promise.all([
          auth.fetchUserProfile(false),
          unifiedShellSession.get('userOrgs', hashedUserId)
        ]);
        profile = cachedProfile;
        const {avatarSrc: userAvatar} = profile || {};
        if (userAvatar && !this.state.userAvatar) {
          this.setState({userAvatar});
        }
        if (auth.allOrgsInProfile(cachedOrgs, profile)) {
          orgsMap = cachedOrgs;
        }
        orgsMap = await staleWhileRevalidate(getOrgsMap, orgsMap);
      }
    } catch {
      // If fresh login, there is no cache so if GQL and IMS api fail, show error screen
      this.showApiFailureErrorScreen('API for getting user orgs failed.');
      return;
    }

    profile = profile || await auth.fetchUserProfile();
    let accessToken = auth.fetchAccessToken();

    // Get user's Spectrum theme from local storage
    // Set state asap to reduce flickers
    let storedThemes = await storage.local.get('spectrumTheme');
    const authId = auth.getHashedAuthId();
    if (storedThemes && storedThemes[authId]) {
      // TODO Remove when we remove feature flag and use strings, not booleans
      let darkestTheme = storedThemes[authId] === 'darkest' ? true : false;
      this.setState({darkestTheme});
    }

    const locale = getBestSupportedLocale(profile, SUPPORTED_LOCALES);
    const productContext = profile.projectedProductContext;

    document.documentElement.setAttribute('lang', locale);

    const allOrgs = new Set;
    let dmaTartanNoTenant = 0;
    const duplicateTenants = {};
    // Grab the tenant ids from the product contexts for all orgs this user
    // is a part of. This will be used to map org id values to tenants for
    // the @value within the path.
    productContext.forEach(ctx => {
      const {services_enabled, owningEntity, serviceCode, tenant_id} = ctx.prodCtx;

      // Keep track of all of the org ids that are in this user's product context.
      allOrgs.add(owningEntity);

      if (serviceCode === 'dma_tartan') {
        // Only update the maps if there is a valid tenant_id. Preventing them
        // from always receiving an undefined tenant_id will protect us from
        // invalid orgs being used and will automatically fall back if this
        // is the selected org.
        if (tenant_id) {
          this.orgTenantMap[owningEntity] = tenant_id;
          // The tenant already exists in the map. This means that there are
          // duplicate tenants in the product context. Track the orgs so that
          // they can be handled later.
          if (this.tenantOrgMap[tenant_id] && this.tenantOrgMap[tenant_id] !== owningEntity) {
            duplicateTenants[tenant_id] = duplicateTenants[tenant_id] || new Set([this.tenantOrgMap[tenant_id]]);
            duplicateTenants[tenant_id].add(owningEntity);
            this.metrics.error(`Duplicate tenant ID: IMS orgs ${owningEntity} and ${this.tenantOrgMap[tenant_id]} both contain dma_tartan and linked to tenant ${tenant_id}`);
          }
          this.tenantOrgMap[tenant_id] = owningEntity;
        } else {
          this.metrics.error(`Invalid tenant ID for org ${owningEntity}`);
          dmaTartanNoTenant++;
        }
      }

      // Create map of org id to list of service codes.
      if (this.userServiceCodes[owningEntity]) {
        this.userServiceCodes[owningEntity].push(serviceCode);
      } else {
        this.userServiceCodes[owningEntity] = [serviceCode];
      }

      // Create map of org id / service code to FIs.
      if (services_enabled) {
        this.userFulfillableItems[owningEntity] = {[serviceCode]: services_enabled.split(',')};
      }
    });

    // Track the number of orgs that a user has with the dma_tartan service
    // code and the number without. This will help us determine the need for
    // this service code in the future.
    const numWithTartan = Object.keys(this.orgTenantMap).length;
    this.metrics.info('User dma_tartan orgs information', {
      noTenant: dmaTartanNoTenant,
      with: numWithTartan,
      without: allOrgs.size - numWithTartan
    });

    // Clean up the maps for duplicate tenants
    Object.keys(duplicateTenants).forEach(tenant => {
      const orgsWithDupeTenant = Array.from(duplicateTenants[tenant]).sort();
      // First sorted tenant is the plain tenant
      this.tenantOrgMap[tenant] = orgsWithDupeTenant[0];
      this.orgTenantMap[orgsWithDupeTenant[0]] = tenant;
      // Loop over the rest of the orgs and update the tenant and org maps with
      // a modified tenant key with the <tenant>:<1st 4 characters of org>.
      orgsWithDupeTenant.slice(1).forEach(org => {
        const tenantKey = `${tenant}:${org.slice(0, 4)}`;
        this.tenantOrgMap[tenantKey] = org;
        this.orgTenantMap[org] = tenantKey;
      });
    });

    const environment = this.config.serviceEnvironment;
    let pathOrg = pathTenant;
    if (pathTenant && pathTenant.indexOf('AdobeOrg') === -1) {
      pathOrg = this.tenantOrgMap[pathTenant];
      pathOrg || this.metrics.error(`User doesn't have access to ${pathTenant} tenant`);
    }
    const tenantInPath = !!pathOrg;

    // Adding `dma_tartan` filtering for the orgsMap here in case it's not
    // filtered properly in GraphQL. `orgTenantMap` only has orgs that have the
    // `dma_tartan` service code.
    orgsMap = orgsMap.filter(({orgRef}) => this.orgTenantMap[`${orgRef.ident}@${orgRef.authSrc}`]);

    let localSession = await unifiedShellSession.get();
    const fallbackOrgId = this.getFallbackOrg(orgsMap);

    let {activeOrg, defaultOrg} = localSession[hashedUserId] || {};

    let selectedImsOrg = fallbackOrgId;
    // Active org from session or SSO handler. Default org from session, SSO handler, or fallback.
    activeOrg = activeOrg || loginOrg;
    defaultOrg = defaultOrg || loginOrg || fallbackOrgId;

    if (this.isValidOrg(orgsMap, pathOrg)) {
      selectedImsOrg = pathOrg;
    } else if (this.isValidOrg(orgsMap, activeOrg)) {
      selectedImsOrg = activeOrg;
    } else if (this.isValidOrg(orgsMap, lastLoggedInOrg)) {
      selectedImsOrg = lastLoggedInOrg;
    } else if (this.isValidOrg(orgsMap, defaultOrg)) {
      selectedImsOrg = defaultOrg;
    }

    // Re-configure network so sandbox GQL call has the correct selectedImsOrg
    this.configureNetwork(selectedImsOrg);

    localSession[hashedUserId] = Object.assign({}, localSession[hashedUserId], {
      activeOrg: selectedImsOrg,
      defaultOrg
    });

    const tenantId = this.orgTenantMap[selectedImsOrg] || selectedImsOrg;
    this.metrics.event('Auth.done', {
      defaultOrg,
      defaultTenant: this.orgTenantMap[defaultOrg],
      selectedOrg: selectedImsOrg,
      selectedTenant: cleanTenant(tenantId)
    });

    this.sessionId = auth.getSessionId();
    if (this.sessionId && !localSession.session) {
      localSession.session = this.sessionId;
    }

    unifiedShellSession.set(localSession);

    const imsOrgs = orgsMap.map(org => ({
      label: org.orgName,
      value: `${org.orgRef.ident}@${org.orgRef.authSrc}`
    }));

    imsOrgs.sort((a, b) => a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1);

    const wasTenantSelected = pathOrg === activeOrg;

    this.getAdditionalUserData(userDataPromise, imsOrgs, profile, locale);

    let showNoAccessShell = !selectedImsOrg;
    // If the user does not have a selected IMS org, that means they don't have
    // any because the fallback picks one. Sets the state so that it will render
    // that view to the user.
    if (showNoAccessShell) {
      this.setState({locale, profile, showNoAccessShell});
      return;
    }
    const selectedImsOrgName = getOrgName(selectedImsOrg, orgsMap);
    this.internal = profile.email && profile.email.indexOf('@adobe') > 0;
    // Falls back to the org id if there is no tenant available for the org
    Internal.setUser({
      accountType: profile.account_type,
      authSystem: 'ims',
      data: {internal: this.internal, tenantId: cleanTenant(tenantId)},
      displayName: profile.displayName,
      groupId: selectedImsOrg,
      groupName: selectedImsOrgName,
      language: locale,
      sessionId: profile.session,
      userId: profile.userId
    });

    const {AppAssemblyService} = await this.postAuth;

    // Instantiate the App Assembly Service when we have the values we need
    this.appAssemblyService = new AppAssemblyService({
      cdn: this.props.config.cdn,
      environment,
      imsOrgs,
      listedApps: this.props.listedApps,
      locale,
      orgTenantMap: this.orgTenantMap,
      profile,
      userFulfillableItems: this.userFulfillableItems,
      userRoles: profile.roles,
      userServiceCodes: this.userServiceCodes
    });

    // Redirect to profile page if profile is in path
    if (getPathWithoutTenant(hashToPath()) === '/profile') {
      return window.location.assign(this.appAssemblyService.getProfileComponent(tenantId).editProfileLink);
    }

    this.setState(
      {
        accessToken,
        imsOrgs,
        locale,
        orgsMap,
        profile,
        selectedImsOrg,
        selectedImsOrgName,
        showNoAccessShell,
        userSandboxes
      },
      async () => {
        this.refreshContext(selectedImsOrg);
        // Update map of org to account in session storage
        const orgAccountMap = {};
        imsOrgs.forEach(({value}) => {
          orgAccountMap[value] = profile.userId;
        });
        storage.session.update('shellOrgAccountMap', orgAccountMap);

        // Notify hidden tabs so they can check if their session is dead.
        // This is needed after account switching (T2E), because any inactive tabs
        // will be running against an invalid token & session. Informing inactive tabs allows
        // them to reload themselves with a token acquired against the active T2E account.
        if (this.props.isLogin && this.sessionId && this.authChannel) {
          this.authChannel.postMessage({
            message: AuthMessages.LOGIN,
            sessionId: this.sessionId
          });
        }

        this.getHeaderData(locale, userDataPromise, profile);

        const {solutionConfig} = this.state;
        // Only await on the feature flag data request when there is a feature
        // flag required to determine access of the current application. Doing
        // it all the time will cause unnecessary delays to other apps.
        const access = solutionConfig.access || {};
        try {
          const ldPromise = this.getLaunchDarklyData();
          access.flag && await ldPromise;
        } catch {
          // Error handling already covered in gelLaunchDarklyData.
          return;
        }

        // The tenant is in the URL and it was not the current IMS org in the session.
        if (tenantInPath && !wasTenantSelected) {
          // If the tenant is already in the URL, we don't need to update the path.
          // Instead, trigger an org change to let IMS know that this is the current org
          await this.onImsOrgChange(selectedImsOrg, activeOrg, true, null, true);
        } else {
          // The tenant is not in the URL or it is and was already the current IMS
          // org in the session. Get shell configuration and update the URL.
          let shellResponse = this.getShellConfiguration(selectedImsOrg);
          const hero = this.getHeroConfig(shellResponse, solutionConfig, this.state.hero);
          this.setState({favicon: hero.icon, hero, shellResponse});
          // If the org isn't authorized for the solution, redirect to home
          if (!this.isOrgPermissionedOnSolution(selectedImsOrg)) {
            updatePath({path: '/home', tenant: tenantId}, true, this.hasTenantInUrl);
            this.metrics.event('Shell.notPermissioned', {appId: solutionConfig.appId, selectedImsOrg});
            await this.showToast('orgHomeRedirectToast', {timeout: TOAST_TIMEOUT, variant: 'info'});
          } else {
            updatePath({tenant: tenantId}, true, this.hasTenantInUrl, this.contextKey);
          }
        }

        authProcessingTimer.time('done');
        this.shellTimer.time(this.props.isLogin ? 'isLoginFlow.done' : 'done', {
          gqlEnabled: true,
          region: this.config.region
        }); // step 9 ready
      }
    );
  }

  /**
   * Get a fallback org in a standard way. Currently it sorts the list of org
   * ids in ascending order and takes the first one.
   * @param {string[]} orgs List of org ids.
   * @returns {string} The fallback org.
   */
  getFallbackOrg = orgs => orgs[0] && `${orgs[0].orgRef.ident}@${orgs[0].orgRef.authSrc}`;

  isValidOrg = (orgs, selectedImsOrg) => selectedImsOrg && orgs.find(org => `${org.orgRef.ident}@AdobeOrg` === selectedImsOrg)

  toggleSideNav = (e, auto) => {
    const open = !this.state.sideNavOpen;
    this.setState({sideNavOpen: open});
    storage.session.set('sideNavOpen', open);
    this.metrics.analytics.trackEvent(getOmegaEvent({
      action: auto ? 'SideNavItem click' : 'hamburger click',
      attributes: {opened: open},
      element: open ? 'nav open' : 'nav close',
      event: e,
      feature: 'left nav',
      type: 'button',
      widget: {name: 'nav interaction', type: auto ? 'SideNavItem' : 'button'}
    }));
  }

  getComponentContext() {
    const {
      activeProductContext,
      accessToken,
      appContainer,
      coachMark,
      coachMarkPosition,
      customSearch,
      darkestTheme,
      environment,
      featureFlags,
      globalDialogOpen,
      imsOrgs,
      locale,
      npsScore,
      orgPermissioned,
      profile,
      sandbox,
      sandboxesAvailable,
      selectedImsOrg,
      selectedImsOrgName,
      selectedSubOrg,
      shellResponse
    } = this.state;

    return {
      activeProductContext,
      appContainer,
      avatar: profile && profile.avatar || '',
      cdn: this.props.config.cdn,
      coachMark,
      coachMarkPosition,
      contextFetchPromise: this.contextFetchPromise,
      contextId: this.contextService?.getId(),
      contextKey: this.contextKey,
      customSearch,
      darkestTheme,
      discoveryService: this.discoveryService,
      environment,
      favicon: this.favicon,
      featureFlags,
      formatMessage: this.props.formatMessage,
      getIframeEntry: this.getIframeEntry,
      getPerformanceMarks: this.getPerformanceMarks,
      getToken: customIms => this.getToken(customIms),
      globalDialogOpen,
      htmlReady: this.htmlReady,
      imsClientId: this.config.ims.client_id,
      imsEnvironment: this.config.ims.environment,
      imsOrg: selectedImsOrg,
      imsOrgName: selectedImsOrgName,
      imsOrgs: imsOrgs.filter(({value}) => /@AdobeOrg$/.test(value)),
      imsProfile: profile,
      imsToken: accessToken,
      internal: this.internal,
      locale,
      metricsEnv: this.props.metricsEnv,
      nonSupportedBrowser: this.nonSupportedBrowser,
      npsScore,
      onSignOut: this.onSignOut,
      orgPermissioned,
      permissionService: this.permissionService,
      preferredLanguages: profile && profile.preferred_languages || [],
      resetConfigStates: (appId, nextAppId, nextSideNav) => this.resetConfigStates(appId, nextAppId, nextSideNav),
      sandbox: sandbox.selected,
      sandboxes: sandbox.sandboxes,
      sandboxesAvailable,
      setConfigStates: config => this.setConfigStates(config),
      setLogoutUrl: url => this.state.auth.logout.add(url),
      setSolutionConfig: this.setSolutionConfig,
      setSubOrgs: this.setSubOrgs,
      settingsService: this.settingsRef,
      shellHeight: sandbox && sandbox.sandboxes && sandbox.sandboxes.length ? SHELL_HEIGHT_WITH_SANDBOX : SHELL_HEIGHT_WITHOUT_SANDBOX,
      shellInfo: shellResponse,
      shellSideNavPresent: this.state.sideNavEnabled && this.state.sidebar.visible !== false && this.state.sideNavOpen,
      shellSideNavWidth: SHELL_SIDE_NAV_WIDTH,
      solutionType: this.config.type,
      subOrg: selectedSubOrg,
      tenant: cleanTenant((this.orgTenantMap || {})[selectedImsOrg] || ''),
      toggleSideNav: this.toggleSideNav,
      translationService: this.translationService
    };
  }

  setConfigStates(config) {
    let {coachMark, customSearch, helpCenter, hero, nps, sidebar} = config;
    if (hero) {
      config.hero = this.getHeroConfig(this.state.shellResponse, this.state.solutionConfig, hero);
    }

    helpCenter && Object.assign(config, {helpCenter: {...this.state.helpCenter, ...helpCenter}});
    nps && Object.assign(config, {nps: this.getNps(nps)});
    customSearch && Object.assign(config, {
      customSearch: {
        // If an enabled value is not explicitly set, maintain the current state
        enabled: customSearch.enabled === undefined ? this.state.customSearch.enabled : customSearch.enabled,
        onClick: this.onCustomSearchClick,
        open: customSearch.open || false
      }
    });

    if (sidebar) {
      Object.assign(config, {
        sidebar: {
          ...sidebar,
          config: sidebar.config || this.state.sidebar.config
        }
      });
      sidebar.collapsed !== undefined && this.setState({sideNavOpen: !sidebar.collapsed});
    }

    this.setState(config);

    coachMark && this.debouncedUpdateTargetNode();
    nps && this.metrics.event('Shell.configuredNps', {isEnabled: nps.enabled});
  }

  resetConfigStates = (appId, nextAppId, nextSideNav) => {
    // Same app is being loaded next. Don't reset so experience doesn't shift.
    if (appId === nextAppId) {
      return;
    }

    // only reset if there's not another sidenav config
    if (!nextSideNav || nextSideNav === {}) {
      this.setState({sidebar: {}});
    }

    this.setState({
      appContainer: null,
      coachMark: {},
      coachMarkPosition: null,
      customButtons: [],
      customEnvLabel: '',
      customHeroClick: null,
      customSearch: {enabled: false, open: false},
      digitalData: null,
      feedback: {},
      globalDialogOpen: false,
      helpCenter: null,
      onCustomButtonClick: NOOP,
      orgToSubOrgsMap: {},
      supportsViewportTakeover: false,
      viewportTakeover: false,
      workspaces: []
    });
  }

  getShellConfiguration(selectedImsOrg) {
    let selectedOrg = selectedImsOrg || this.state.selectedImsOrg;
    return this.appAssemblyService.fetchShell(
      selectedOrg,
      this.orgTenantMap[selectedOrg]
    );
  }

  onImsOrgChange = async (selectedImsOrg, currentImsOrg, replace, selectedSubOrg, init) => {
    if ((this.contextService || {}).isSubOrg && selectedSubOrg && selectedImsOrg === currentImsOrg) {
      return this.contextService.change(selectedImsOrg, selectedSubOrg);
    }

    try {
      unifiedShellSession.update('activeOrg', selectedImsOrg, this.state.auth.getHashedUserId());
      this.metrics.event('Shell.orgChange', {currentImsOrg, selectedImsOrg, selectedSubOrg});
      selectedSubOrg && (this.contextService || {}).isSubOrg && this.contextService.change(selectedImsOrg, selectedSubOrg);
      const selectedImsOrgName = getOrgName(selectedImsOrg, this.state.orgsMap);
      Internal.setUser({
        data: {internal: this.internal, tenantId: cleanTenant(this.orgTenantMap[selectedImsOrg])},
        groupId: selectedImsOrg,
        groupName: selectedImsOrgName
      });

      this.settingsRef.settingsHandler({
        groupId: PREF_GROUPID,
        level: SettingsLevel.USER,
        settings: {lastLoggedInOrg: selectedImsOrg}
      }, 'POST', true, PREF_APPID);

      const state = {
        selectedImsOrg,
        selectedImsOrgName,
        selectedSubOrg
      };
      this.configureNetwork(selectedImsOrg);
      // Only fetch launch darkly on non-init (authorize) workflows because
      // authorize does it already and onImsOrgChange only happens when the
      // org needs to be changed due to the path's tenant.
      if (!init) {
        const ldData = await this.getLaunchDarklyData(false, selectedImsOrg, selectedImsOrgName);
        Object.assign(state, ldData);
      }

      this.setState(state, () => {
        if (!this.isOrgPermissionedOnSolution(selectedImsOrg)) {
          updatePath({path: '/home', tenant: this.orgTenantMap[selectedImsOrg]}, replace, this.hasTenantInUrl);
          this.showToast('orgSwitchRedirectToast', {timeout: TOAST_TIMEOUT, variant: 'info'});
          this.metrics.event('Shell.notPermissioned', {appId: this.state.solutionConfig.appId, currentImsOrg, selectedImsOrg});
        }
      });
    } catch (error) {
      this.metrics.error(error.message);
      await this.showToast('orgFailToast', {variant: 'error'});
      return;
    }

    // On org change, re-fetch current URL context.
    this.refreshContext();

    // Clear Active Product Context
    selectedSubOrg || this.setState({activeProductContext: null});

    this.metrics.analytics.trackPage();

    // if org changed and no sub org selected, reset selectedSubOrg
    if (selectedImsOrg !== currentImsOrg && !selectedSubOrg) {
      this.setState({selectedSubOrg: {id: null, name: null}});
    }
    return true;
  }

  /**
   * Checks whether an ims org is permissioned on a given solution.
   * @param {string} orgId The IMS org.
   * @param {Object} solutionConfig Optional solution config.
   * @param {Object=} sandboxOverride Optional sandbox to use instead of currently
   *   set sandbox.
   * @returns {boolean} If the org is permissioned.
   */
  isOrgPermissionedOnSolution(orgId, solutionConfig, sandboxOverride) {
    const {featureFlags, sandbox, solutionConfig: config} = this.state;
    const {access} = solutionConfig || config;
    const selectedSandbox = sandboxOverride || sandbox?.selected;
    const orgPermissioned = hasPermission(access, featureFlags, this.userFulfillableItems[orgId], this.userServiceCodes[orgId], selectedSandbox);
    this.setState({orgPermissioned});
    return orgPermissioned;
  }

  /**
   * Displays a toast with a custom message.
   * @param {string} messageId The message ID of the localized string to populate the toast.
   * @param {Object} options Optional object of objects such as variant.
   */
  async showToast(messageId, options) {
    const {showShellToast} = await import('./modules/ShellToast');
    showShellToast(messageId, this.state.locale, options);
  }

  updateUrlOnOrgChange = (selectedImsOrg, currentImsOrg, replace, selectedSubOrg) => {
    // Store the suborg so it is available in onHistoryChange
    this.updatedSubOrg = selectedSubOrg;
    // Since the org has not changed, the subOrg must have changed. We must update the path before updating
    // state or localStorage to enable navigation blocking for subOrgs.
    if (selectedImsOrg === currentImsOrg) {
      return updateValueInPath(this.contextKey, selectedSubOrg.orgIdName, this.hasTenantInUrl, false);
    }

    // If the `selectedImsOrg` ends in AdobeID, that means it's an attempt to
    // load an account instead of an org. This will only happen in the following
    // scenarios:
    //  - In Type1 and click on Type2E org in switcher
    //  - In Type2E and click on Type2E org in switcher
    //  - In Type2E and click on Type1 account in switcher
    if (!(/@AdobeOrg$/.test(selectedImsOrg))) {
      return this.onType2eOrgChange(selectedImsOrg);
    }

    updatePath({tenant: this.orgTenantMap[selectedImsOrg] || selectedImsOrg}, replace, this.hasTenantInUrl, this.contextKey);
    // An org switch for a tenantless solution won't change the URL, so no history update is fired.
    // We can get around this by manually switching the org
    if (this.state.solutionConfig.hideTenant) {
      this.onImsOrgChange(selectedImsOrg, currentImsOrg, true, selectedSubOrg);
    }
  }

  onType2eOrgChange = async selectedImsOrg => {
    const {auth} = this.state;
    this.metrics.info('Switching accounts', {from: auth.getHashedUserId(), to: selectedImsOrg});
    try {
      await auth.switchProfile(selectedImsOrg);
    } catch {
      return this.showToast('type2eError', {variant: 'error'});
    }
    await this.authProcessing();
    updatePath({tenant: this.orgTenantMap[this.state.selectedImsOrg]}, false, this.hasTenantInUrl, this.contextKey);
  }

  onHistoryChange = async location => {
    let pathTenant = getTenantFromPath(location.pathname, true);
    let pathOrg = this.tenantOrgMap[pathTenant];
    let pathSubOrg = getValueFromPath(location.pathname, this.contextKey);
    const isSubOrgService = (this.contextService || {}).isSubOrg;
    const {orgToSubOrgsMap, sandbox, selectedImsOrg, selectedSubOrg} = this.state;

    this.contextService && this.contextService.onHistoryChange(location, {orgToSubOrgsMap, sandbox, selectedImsOrg, selectedSubOrg});

    // If the org is not set, update the URL with the current one.
    if (!pathOrg && selectedImsOrg) {
      setTimeout(() => updatePath({tenant: this.orgTenantMap[selectedImsOrg] || selectedImsOrg}, true, this.hasTenantInUrl, this.contextKey), 0);
    }
    // Compare the new org with the current one. If they are different, trigger an org change.
    // For a solution that supports suborgs, compare the pathSubOrg with the current one.
    // If they are both present and different, trigger an org change. If there is no pathOrg,
    // that means the solution has hideTenant and this can be skipped when tenant id isn't
    // the part of the history that changed.
    if ((pathOrg && pathOrg !== selectedImsOrg) ||
      (isSubOrgService && pathSubOrg && selectedSubOrg && pathSubOrg !== selectedSubOrg.orgIdName)) {
      // If the new org is from a different account, trigger a type2e switch.
      const orgAccountMap = await storage.session.get('shellOrgAccountMap') || {};
      const requestedAccount = orgAccountMap[pathOrg];
      if (orgAccountMap[selectedImsOrg] !== requestedAccount) {
        return this.onType2eOrgChange(requestedAccount);
      }
      // Replace argument is `true` because the URL has already been updated,
      // so we don't want to push anything else to the history.
      return this.onImsOrgChange(pathOrg, selectedImsOrg, true, this.updatedSubOrg);
    }
  }

  /**
   * Signout method that triggers the actual IMS logout. If the event is triggered by the user
   * via the signout button, the iframe is not killed via onLogout. This allows blockNavigation
   * to work correctly. In all other cases, the iframe is killed and the ims signout process is
   * kicked off.
   * @param {boolean} authExpired false means the event was triggered by the user.
   * @param {String} message Descriptive message to be sent to metrics.
   */
  silentSignOut = async (authExpired, message = 'User signed out') => {
    authExpired && this.props.onLogout();

    this.metrics.event('Shell.signOut', undefined, message);
    await this.state.auth.signOut();
  }

  /**
   * Signout method triggered by signout button or solution triggered event.
   * @param {boolean} authExpired false means the event was triggered by the user.
   * @param {String} message Descriptive message to be sent to metrics.
   */
  onSignOut = async (authExpired, message = 'User signed out') => {
    this.silentSignOut(authExpired, message);
    this.authChannel && this.authChannel.postMessage({message: AuthMessages.LOGOUT});
  }

  onCustomSearchClick = () => {
    const {enabled, onClick, open} = this.state.customSearch;
    open && this.metrics.analytics.trackEvent({
      action: 'click',
      element: 'custom search',
      feature: 'shell header',
      type: 'button',
      widget: {name: 'custom search', type: 'button'}
    });
    this.setState({customSearch: {enabled, onClick, open: !open}});
  }

  getHeroConfig = (shellResponse, solutionConfig, stateHero) => {
    // Grab the current solution or service in order to get the proper name string
    const allSolutionsAndServices = (shellResponse.solutions || []).concat(shellResponse.services || []);
    const cjmPath = hashToPath().includes('/cjm/');
    const current = allSolutionsAndServices.find(solServ => {
      const apps = Array.isArray(solServ.appId) ? solServ.appId : [solServ.appId];
      return apps.indexOf(cjmPath ? 'cjm-home' : solutionConfig.appId) > -1;
    });

    let newHero = HERO_CONFIGS.cloud;
    const type = (current && current.adobeBrandType) || 'cloud';
    let {longName, name} = current || {};

    if (type !== 'cloud' || longName === 'Admin Tool' || longName === 'Adobe Journey Optimizer') {
      // State hero is used here to push runtime changes forward in case getHeroConfig
      // is called in such a way that App Assembly load or a Solution config change occurs
      // after a meaningful runtime change has been made.
      let runtimeOverrides = {};
      if (stateHero) {
        runtimeOverrides = {path: stateHero.path};
        if (solutionConfig?.sandbox?.thirdParty) {
          runtimeOverrides = stateHero;
        }
      }

      const baseHeroConfig = HERO_CONFIGS[type];
      // Precedence: Runtime set hero values, solution set hero values, default type values.
      newHero = Object.assign({}, baseHeroConfig, {
        // shortTitle / name is the only required label for a solution, so all other labels fallback to it
        // or are generated from it.
        shortTitle: name || baseHeroConfig.shortTitle,
        title: longName || name || baseHeroConfig.title
      }, runtimeOverrides);
    }

    // Find and set the favicon to the new hero icon
    document.querySelectorAll('#favicon').forEach(link => {
      link.href = newHero.favicon;
    });

    return newHero;
  }

  onSandboxChange = selected => {
    const {sandbox, selectedImsOrg} = this.state;
    this.permissionService.setContext(selectedImsOrg, selected);
    this.contextService.change(selectedImsOrg, selected, sandbox);
  }

  /**
   * Fetches the current URL context, e.g. sandboxes or suborgs
   * @param {string} selectedImsOrg The current ims org.
   * subOrgs on login and we need to select the subOrg associated with the login APC.
   */
  refreshContext(selectedImsOrg = this.state.selectedImsOrg) {
    const {featureFlags, orgToSubOrgsMap, sandbox, selectedSubOrg, solutionConfig, userSandboxes} = this.state;
    this.contextService && (this.contextFetchPromise = this.contextService.fetch({
      featureFlags,
      orgToSubOrgsMap,
      sandbox,
      selectedImsOrg,
      selectedSubOrg,
      solutionConfig,
      userSandboxes
    }));
  }

  /**
   * Sets up the context service
   * @param {Object} solutionConfig The solution config of the application.
   */
  setupContextService = solutionConfig => {
    this.contextService = getContextService(
      solutionConfig,
      ctx => this.setState(ctx, () => {
        if (!this.contextService?.isSandbox) {
          return;
        }
        this.configureNetwork();

        const {selectedImsOrg, tenant} = this.state;
        if (!this.isOrgPermissionedOnSolution(selectedImsOrg, solutionConfig)) {
          setTimeout(() => {
            updatePath({path: '/home', tenant}, true, true);
            this.metrics.event('Shell.notPermissioned', {appId: solutionConfig.appId, selectedImsOrg});
            this.showToast('orgHomeRedirectToast', {timeout: TOAST_TIMEOUT, variant: 'info'});
          }, 100);
        }
      }),
      {settingsService: this.settingsRef}
    );
    this.contextKey = this.contextService && this.contextService.getUrlKey();
    this.contextService?.isSandbox && this.setState({sandboxesAvailable: true});
  }

  setSolutionConfig = async solutionConfig => {
    const {
      featureFlags,
      hero,
      sandbox,
      selectedImsOrg,
      shellResponse,
      sidebar
    } = this.state;

    this.setupContextService(solutionConfig);

    const heroConfig = this.getHeroConfig(shellResponse, solutionConfig, hero);
    // Because we don't clear sandboxes on solution change we must reset the sandboxes if the new
    // solution doesn't support them.
    const sandboxConfig = (this.contextService || {}).isSandbox ?
      Object.assign(sandbox, {enabled: true, onChange: this.onSandboxChange}) :
      {enabled: false, onChange: () => {}, sandboxes: [], selected: null};

    let sideNavOpen = await this.getSideNavState();
    if ((solutionConfig.sideNav || {}).closeOnLoad) {
      sideNavOpen = false;
    }

    this.setState({
      favicon: hero.icon,
      helpCenter: {
        solution: solutionConfig.appId
      },
      hero: heroConfig,
      nps: this.getNps(solutionConfig.nps),
      orgPermissioned: false,
      releaseType: solutionConfig.releaseType,
      sandbox: sandboxConfig,
      sidebar: {...sidebar, config: solutionConfig.sideNav || null},
      sideNavEnabled: !!solutionConfig.sideNav,
      sideNavOpen,
      solutionConfig
    }, () => {
      let enabledSandbox = null;

      // If the solution should be restricted by sandbox, check the feature flag
      // to get the enabled sandboxes. It validates them against the sandboxes
      // the user has and selected a valid one.
      if (featureFlags && solutionConfig?.restrictBySandbox) {
        const {access: {sandboxFlag}} = solutionConfig;
        const {sandboxes, selected} = sandboxConfig;
        enabledSandbox = getEnabledSandbox(sandboxes, featureFlags[sandboxFlag], selected);

        // If the currently selected sandbox is not the same as the enabled
        // sandbox tied to the feature flag, change the sandbox so that it's
        // valid for the CJM application.
        if (selected?.name && enabledSandbox?.name && selected.name !== enabledSandbox.name) {
          this.onSandboxChange(enabledSandbox);
        }
      }

      // updatePath triggers onHistoryChange which relies on solution config values from the state.
      // This code has been moved to the callback of setState so we can ensure that the new solution config values
      // have been set in state before onHistoryChange is called.
      this.metrics.event('Shell.solutionConfig.set', {solutionConfig});
      this.hasTenantInUrl = !solutionConfig.hideTenant;
      const tenant = this.orgTenantMap[selectedImsOrg] || selectedImsOrg;
      // Check if the org is allowed to access the new solution.
      if (selectedImsOrg && !this.isOrgPermissionedOnSolution(selectedImsOrg, solutionConfig, enabledSandbox)) {
        updatePath({path: '/home', tenant}, true, true);
        this.metrics.event('Shell.notPermissioned', {appId: solutionConfig.appId, selectedImsOrg});
        return this.showToast('orgHomeRedirectToast', {timeout: TOAST_TIMEOUT, variant: 'info'});
      }
      // If the selected sandbox is defined, the previous solution and the new solution both support sandboxes
      // therefore we don't need to fetch sandboxes again. Only fetch if coming from a solution without sandboxes to a
      // solution with sandboxes enabled and the imsOrg is set (it is not a refresh or login).
      sandboxConfig.selected || selectedImsOrg && this.refreshContext();
      updatePath({tenant}, true, this.hasTenantInUrl, this.contextKey);
      this.state.sideNavEnabled && this.setWorkHub();
      // Initial state changes and data fetching should be complete
      if (Core.firstRender) {
        Core.firstRender = false;
        this.onReady();
      }
    });
  }

  /**
   * Handle actions that should happen once after firstRender and state changes
   */
  onReady = async () => {
    // If the user has not completed an Product Usage Consent we need to show one
    // TODO Fetch hasUserConsent from GraphQL
    const showUserConsent = false;
    if (showUserConsent) {
      this.showUserConsent();
    }
  }

  getNps = newNps => {
    const forceNps = getQueryValue('forcenps');
    const nps = Object.assign({
      enabled: false,
      onSubmit: npsScore => this.setState({npsScore}),
      sampling: 10,
      showToAdobeUsers: true
    }, newNps || {});
    forceNps && Object.assign(nps, {force: forceNps === 'true'});
    return nps;
  }

  updateTargetNode = () => {
    const {coachMark} = this.state;
    if (!coachMark.enabled) {
      return;
    }
    const targetNode = document.querySelector(coachMark.element);
    if (!targetNode) {
      this.setState({coachMark: {...coachMark, hidden: true}});
      return;
    }
    // Get the position of the leftmost edge of the element and the width of the element
    const {left, width} = targetNode.getBoundingClientRect();
    // Calculation for the middle of the element minus indicator width
    const coachMarkPosition = left + (width / 2) - 16;
    this.setState({coachMark: {...coachMark, hidden: false}, coachMarkPosition});
    return coachMarkPosition;
  }

  /**
   * Read/Upsert/Delete settings.
   * @param {Object} data Required: JSONObject contaning requested groupId,
   * settings, and corresponding settings level(optional).
   * GET ex- {groupId: 'group1', level: SettingsLevel.USER,
   * settings: {key1: default value to fallback}}.
   * POST ex- {groupId: 'group1', settings: {key1: value1}}.
   * @param {string} method Required: Method Type: GET/POST/DELETE.
   * @param {Object} isUnifiedShell Optional: To be set to true
   * by unified shell apps to consume Unified Shell Application space.
   * @param {Object} appId - Optional: Used by unifiedShell apps to
   * consume settings from different app space overriding unified shell app space.
   * ex- settingsHandler(data, 'GET', false, 'not-unifiedshell-appId')
   * @param {boolean} enableSandboxForDifferentAppSpace - Optional: Used by unifiedShell apps that
   * consume settings from Unified Shell Application space or
   * different app space overriding unified shell app space to enable sandbox default: false.
   * ex- settingsHandler(data, 'GET', false, 'not-unifiedshell-appId', true)
   *
   * @returns {Promise} - Settings Response.
   */
  async settingsHandler(data, method, isUnifiedShell, appId, enableSandboxForDifferentAppSpace) {
    const {auth, solutionConfig, selectedImsOrg, sandbox} = this.state;
    const environment = this.config.serviceEnvironment;
    let accessToken = auth.fetchAccessToken();
    const clientId = auth.getClientId();
    const selectedAppId = appId || isUnifiedShell && UNIFIED_SHELL_APPID
    // pick solutionConfig.settingsAppId if present rather than solutionConfig.appId
    || solutionConfig.settingsAppId || solutionConfig.appId;
    const settingsPackageLoadTimer = this.metrics.start('SettingsLoadJS');
    const SettingsService = await import('@exc/settings');
    settingsPackageLoadTimer.time('done', `Core: Settings Request for appId: ${selectedAppId} and orgId: ${selectedImsOrg}`);
    SettingsService.configure({
      env: environment,
      imsInfo: {accessToken, clientId}
    });
    let selectedSandbox = undefined;
    if (sandbox && sandbox.selected && !sandbox.selected.isDefault && (!(appId || isUnifiedShell && UNIFIED_SHELL_APPID) || enableSandboxForDifferentAppSpace)) {
      selectedSandbox = sandbox.selected.name;
    }
    return await SettingsService.settingsHandler({
      appId: selectedAppId,
      data,
      imsOrgId: selectedImsOrg,
      method,
      sandbox: selectedSandbox
    });
  }

  /**
  * Used to trigger actions dependent on feature flags
  */
  onFeatureFlags = () => {
    // TODO Remove after theme feature flag removed;
    this.userThemeHandler();
    // TODO Remove after user consent feature flag removed;
    this.showUserConsent();
    // TODO Remove after service worker rollout.
    this.registerServiceWorker();
  }

  /**
  * Handles shell theme on load and on toggle by user.
  * @param {string} theme Optional, theme string indicating new spectrum theme
  * @param {boolean} onToggle The user is toggling the theme
  */
  userThemeHandler = async (theme, onToggle) => {
    const {auth, featureFlags, profile, settings} = this.state;
    // TODO Remove the feature flag check for the user set theme
    if (!profile || !settings || !featureFlags || featureFlags['theme-support'] === 'false') {
      return;
    }
    // If no theme is given, settings service is the source of truth
    if (!theme) {
      theme = settings.theme;
    }
    // TODO Update state value to be a string rather than boolean when feature
    // flag is removed, it is currently needed to support in between states
    let darkestTheme = this.state.darkestTheme ? 'darkest' : 'lightest';
    // If localStorage (state) and theme match and this is not a user toggle
    // from the profile popover, then we do nothing
    if (!onToggle && (darkestTheme === theme)) {
      return;
    }
    // If they do NOT match, then theme (settings service || user toggled value)
    // is the source of truth and we update the state, localStorage and settings
    // TODO Remove when feature flag is removed and we use only strings for theme
    let newTheme = theme === 'darkest' ? true : false;
    this.setState({darkestTheme: newTheme});
    storage.local.set('spectrumTheme', {[auth.getHashedAuthId()]: theme}, TEN_YEARS);
    this.settingsRef.settingsHandler({
      groupId: PREF_GROUPID,
      level: SettingsLevel.USER,
      settings: {theme}
    }, 'POST', true, PREF_APPID);
  }

  /**
   * Gets token from IMS.
   * @param {string} clientId Client id string.
   * @param {string} scope List of scopes.
   * @returns {Object} Profile, token.
   */
  async getTokenFromIMS(clientId, scope) {
    const {auth} = this.state;

    try {
      const customTokenTimer = this.metrics.start('CustomToken', {clientId});
      const res = await auth.fetchCustomImsAccessToken(clientId, scope);
      customTokenTimer.time('done', {clientId, success: (res && !!res.access_token)});
      const {access_token, ...profile} = res;
      return {profile, token: access_token};
    } catch (e) {
      if (e && e.error && e.jump) {
        this.metrics.event(
          'Core.tokenRideError',
          `${clientId} token error: ${e.error}`,
          {level: Level.ERROR}
        );
      } else {
        this.metrics.error(`Failed to fetch token for client id: ${clientId}`, e);
      }
      return null;
    }
  }

  /**
   * Gets token for re-auth from cache or IMS.
   * @param {string} client_id Client id string.
   * @param {string} scope List of scopes.
   * @returns {Object} Profile, token.
   */
  async getToken({client_id, scope}) {
    await this.authPromise;
    const {auth} = this.state;
    const hashedUserId = auth.getHashedUserId();
    const cachedInfo = await this.getCachedToken(client_id, scope, hashedUserId);

    // If the cached token is available, return it.
    if (cachedInfo) {
      return cachedInfo;
    }

    // Prevent duplicate calls by storing promises.
    const key = btoa(client_id + scope + hashedUserId);
    this.tokenPromises = this.tokenPromises || {};
    let tokenPromise = this.tokenPromises[key];

    // If there is an existing promise inflight, wait and return it.
    if (tokenPromise) {
      return await tokenPromise;
    }

    // Create a promise and store it to prevent redundant calls.
    this.tokenPromises[key] = tokenPromise = this.getTokenFromIMS(client_id, scope);

    // Need to asynchronously fetch the token from IMS for the custom client id.
    // Upon completion, sends the payload if one is queued.
    const newIms = await tokenPromise;
    newIms && this.updateCachedTokens(client_id, scope, hashedUserId, newIms);

    delete this.tokenPromises[key];
    return newIms;
  }

  /**
   * Gets cached token for sandbox re-auth. Should retrieve from cache if
   * switching solutions within 10 minutes.
   * @param {string} clientId Client id string.
   * @param {string} scope List of scopes.
   * @param {string} hashedUserId Hashed userId.
   * @returns {Object} Profile, token, and date info.
   */
  getCachedToken = async (clientId, scope, hashedUserId) => {
    let cachedTokens = await storage.session.get('shellCachedTokens') || {};
    let key = btoa(clientId + scope + hashedUserId);

    if (!(key in cachedTokens)) {
      return;
    }

    let info = cachedTokens[key];
    let dateDiff = Date.now() - info.dateStored;
    if (dateDiff < TEN_HOURS) {
      this.updateCachedTokens(clientId, scope, hashedUserId, info);
      return info;
    }
    // clear expired token by passing no token
    this.updateCachedTokens(clientId, scope, hashedUserId);
  }

  /**
   * Sets and updates cached token for sandbox re-auth. Stores token with
   * current date using clientId and scopes.
   * @param {string} clientId Client id string.
   * @param {string} scope List of scopes.
   * @param {string} hashedUserId Hashed userId.
   * @param {Object} ims Ims object with profile and token info.
   */
  updateCachedTokens = async (clientId, scope, hashedUserId, ims) => {
    let cachedTokens = await storage.session.get('shellCachedTokens') || {};
    let key = btoa(clientId + scope + hashedUserId);
    if (!ims) {
      delete cachedTokens[key];
    } else {
      cachedTokens[key] = {
        dateStored: Date.now(),
        profile: ims.profile,
        token: ims.token
      };
    }
    storage.session.set('shellCachedTokens', cachedTokens, TEN_HOURS);
  }

  renderNoAccess() {
    const {
      defaultComponent,
      hasAccountClusterData,
      locale,
      profile,
      userAccounts
    } = this.state;
    if (!hasAccountClusterData) {
      return defaultComponent;
    }

    const account = userAccounts.find(({userId, userType}) => userId === profile.userId && userType !== AUTH_TYPE.TYPE2E);
    const hasOtherAccounts = account && userAccounts.length > 1;

    return (
      <React.Suspense fallback={defaultComponent}>
        <ShellNoAccess hasOtherAccounts={!!hasOtherAccounts} locale={locale} />
      </React.Suspense>
    );
  }

  registerServiceWorker = async () => {
    const {featureFlags} = this.state;
    // The flag has 3 possible values: "ON", "OFF" and "NA".
    // NA is ignored, ON registers and OFF unregisters (Kill switch).
    const serviceWorkersEnabled = featureFlags?.['service-workers-enabled'] || '';
    if (serviceWorkersEnabled === 'OFF') {
      return this.serviceWorkerService.unregister();
    }
    if (serviceWorkersEnabled === 'ON') {
      return this.serviceWorkerService.registerServiceWorker();
    }
  }

  /**
   * Import and show the User Consent form
   * @param {boolean} forceUserConsent Forces display of user consent modal.
   */
  showUserConsent = async () => {
    const {featureFlags} = this.state;
    // Until the user consent form is released, we need the feature flag
    const enableUserConsent = featureFlags && featureFlags['user-consent-enabled'] === 'true';
    // Support forcing user consent with a query param for user testing
    const forceUserConsent = getQueryValue('forceuserconsent') === 'true';
    // Early return if the user does not have the feature flag AND is not forcing the consent modal
    // OR return early if the modal is already displayed
    if (this.userConsentKey || (!enableUserConsent && !forceUserConsent)) {
      return;
    }
    // Early return if this is the user preferences page, because a user can open the preferences
    // page from the user consent modal and we don't want to show it again in that case
    if (getPathWithoutTenant(hashToPath()) === '/preferences') {
      return;
    }
    // Show the user consent modal
    this.modalContainer = (await import('@react/react-spectrum/ModalContainer')).default;
    const ConsentDialog = (await import('./modules/UserConsent')).default;
    this.userConsentKey = this.modalContainer.show(
      <ConsentDialog
        disableEscKey
        hide={this.hideUserConsent}
        locale={this.props.locale} />
    );
  }

  hideUserConsent = data => {
    // TODO Send data value to GraphQL
    console.log(data); // eslint-disable-line no-console
    this.modalContainer.hide(this.userConsentKey);
    this.modalContainer = null;
    this.userConsentKey = null;
  }

  render() {
    const {
      appContainer,
      accessToken,
      basePath,
      coachMark,
      customEnvLabel,
      customHeroClick,
      customButtons,
      customSearch,
      darkestTheme,
      defaultComponent,
      digitalData,
      enhancedImsOrgs,
      environment,
      error,
      featureFlags,
      feedback,
      globalDialogOpen,
      hasAccountClusterData,
      helpCenter,
      hero,
      imsOrgs,
      locale,
      modal,
      nps,
      onCustomButtonClick,
      orgToSubOrgsMap,
      profile,
      pulseLoaded,
      releaseType,
      sandbox,
      selectedImsOrg,
      selectedSubOrg,
      settingsResponse,
      shellResponse,
      showNoAccessShell,
      sidebar,
      sideNavEnabled,
      sideNavOpen,
      solutionConfig,
      solutionLoaded,
      supportsViewportTakeover,
      timelineResponse,
      userAvatar,
      userOrgPreferences,
      viewportTakeover,
      workHubResponse,
      workspaces
    } = this.state;

    // Until HUB UI is released, we need the 'work-hub-enabled' flag to set
    // the UI for ShellAppSwitcher or HUB UI navigation accordingly
    // This code to be removed when HUB UI is released
    let enableWorkHub = featureFlags && featureFlags['work-hub-enabled'] === 'true';
    // Until Light/Dark theme is released, we need the 'theme-support' flag to set
    // the UI for Light/Dark theme accordingly
    // This code to be removed when Light/Dark theme is released
    let enableThemes = featureFlags && featureFlags['theme-support'] === 'true';
    // If the solution restricts the sandboxes available to a user via feature
    // flag, this pulls them from the associated feature flag, creating an array
    // to be used to filter the sandbox UI.
    const {access, restrictBySandbox} = solutionConfig || {};
    const filteredSandboxes = restrictBySandbox && featureFlags && featureFlags[access?.sandboxFlag].split(',');
    // Until profile refactor is released, we will keep this behind a feature flag.
    let enableNewProfilePopover = featureFlags && featureFlags['new-profile-enabled'] === 'true';

    let content = this.props.children;
    showNoAccessShell && (content = this.renderNoAccess());
    error && (content = <ErrorView type={error} />);

    const allOrgs = enhancedImsOrgs.length ? enhancedImsOrgs : imsOrgs || [];
    // This does the equivalent of a lodash groupBy(customButtons, 'scope');
    const scopedCustomButtons = (customButtons || []).reduce((r, v, i, a, k = v['scope']) => ((r[k] || (r[k] = [])).push(v), r), {});
    const globalSearchConfig = {
      accessToken,
      enabled: (solutionConfig.unifiedSearch || {}).enabled && accessToken && (this.state.featureFlags || {})['unified-search'] === 'true',
      selectedImsOrg
    };

    let providerProps = {
      locale,
      toastPlacement: 'top center'
    };

    providerProps = enableThemes ?
      {...providerProps, theme: darkestTheme ? 'darkest' : 'light'} :
      providerProps;

    return (
      <Provider {...providerProps}>
        <CoreContext.Provider value={this.getComponentContext()}>
          <ShellWrapper
            accessToken={accessToken}
            appContainer={appContainer}
            appId={solutionConfig.appId}
            basePath={basePath}
            clientId={this.config.ims.client_id}
            coachMark={coachMark}
            customButtons={scopedCustomButtons}
            customEnvLabel={customEnvLabel}
            customHeroClick={customHeroClick}
            customSearch={customSearch}
            darkestTheme={darkestTheme}
            defaultComponent={defaultComponent}
            digitalData={digitalData}
            enableNewProfilePopover={enableNewProfilePopover}
            enableThemes={enableThemes}
            enableWorkHub={enableWorkHub}
            environment={environment}
            featureFlags={featureFlags}
            feedback={feedback}
            filteredSandboxes={filteredSandboxes}
            globalDialogOpen={globalDialogOpen}
            globalSearch={globalSearchConfig}
            hasAccountClusterData={hasAccountClusterData}
            helpCenter={helpCenter}
            hero={hero}
            imsOrgs={allOrgs}
            locale={locale}
            modal={modal}
            noAccess={showNoAccessShell}
            noSolution={!solutionConfig || !solutionConfig.path}
            nps={nps}
            onCustomButtonClick={onCustomButtonClick}
            onGlobalDialogOpen={globalDialogOpen => this.setState({globalDialogOpen})}
            onImsOrgChange={this.updateUrlOnOrgChange}
            onSetUserTheme={this.userThemeHandler}
            onSignOut={this.onSignOut}
            orgToSubOrgsMap={orgToSubOrgsMap}
            profile={profile}
            pulse={{
              accessToken,
              enabled: !!pulseLoaded && !!accessToken && Object.keys(shellResponse).length > 0,
              environment: this.config.serviceEnvironment,
              locale,
              profile,
              selectedImsOrg,
              settingsResponse,
              settingsService: this.settingsRef,
              shellResponse,
              timelineResponse,
              userOrgPreferences
            }}
            releaseType={releaseType}
            sandbox={sandbox}
            selectedImsOrg={selectedImsOrg}
            selectedSubOrg={selectedSubOrg}
            selectedTenantId={cleanTenant(this.orgTenantMap[selectedImsOrg])}
            serviceEnvironment={this.config.serviceEnvironment}
            settingsService={this.settingsRef}
            shellResponse={shellResponse}
            sideNavConfig={sidebar.config}
            sideNavEnabled={sideNavEnabled && sidebar.visible !== false}
            sideNavOpen={sideNavOpen}
            solutionLoaded={solutionLoaded}
            supportsViewportTakeover={supportsViewportTakeover}
            toggleSideNav={this.toggleSideNav}
            translationService={this.translationService}
            userAvatar={userAvatar}
            viewportTakeover={viewportTakeover}
            workHubResponse={workHubResponse}
            workspaces={workspaces}
          >
            <React.Fragment>
              {content}
            </React.Fragment>
          </ShellWrapper>
        </CoreContext.Provider>
      </Provider>
    );
  }
}

Core.propTypes = {
  auth: PropTypes.object,
  config: PropTypes.object,
  isLogin: PropTypes.bool,
  metrics: PropTypes.object,
  org: PropTypes.string
};

export default withLocalization(Core, {displayName: 'Core'});
